import { GridColDef } from "@mui/x-data-grid";
import { formatDate } from "../../../helpers/formatingFunctions";
import UpdateLoanPopup from "../../../components/Fleet/UpdateLoanPopup/UpdateLoanPopup";
import ConfirmActionButton from "../../../components/Fleet/ConfirmActionButton/ConfirmActionButton";
import { Box, Tooltip, Typography } from "@mui/material";

const clickableCellStyle = {
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
};

const formatCurrencyValue = (value: number | string) =>
  Number(value).toLocaleString("en-US", { minimumFractionDigits: 2 });

export const getLoanTableColumns = (selectedDate: string): GridColDef[] => [
  {
    field: "loanFlag",
    headerName: "Ownership",
    width: 100,
    renderCell: (params) => <Box sx={clickableCellStyle}>{params.value}</Box>,
  },
  {
    field: "lenderName",
    headerName: "Lender Name",
    width: 150,
    renderCell: (params) => <Box sx={clickableCellStyle}>{params.value}</Box>,
  },
  {
    field: "loanAmount",
    headerName: "Loan Amount",
    width: 100,
    type: "number",
    valueFormatter: (v) =>
      Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 }),
  },
  {
    field: "loanNumber",
    headerName: "Loan Number",
    width: 150,
    renderCell: (params) => <Box sx={clickableCellStyle}>{params.value}</Box>,
  },
  {
    field: "loanStartDate",
    headerName: "Start Date",
    width: 150,
    valueFormatter: (value) => formatDate(value as string),
    renderCell: (params) => (
      <Box sx={clickableCellStyle}>{params.formattedValue}</Box>
    ),
  },
  {
    field: "loanEndDate",
    headerName: "End Date",
    width: 150,
    valueFormatter: (value) => formatDate(value as string),
    renderCell: (params) => (
      <Box sx={clickableCellStyle}>{params.formattedValue}</Box>
    ),
  },
  {
    field: "loanTermMonths",
    headerName: "Term (Months)",
    width: 120,
    renderCell: (params) => <Box sx={clickableCellStyle}>{params.value}</Box>,
  },
  {
    field: "interestRate",
    headerName: "Interest Rate",
    width: 120,
    renderCell: (params) => <Box sx={clickableCellStyle}>{params.value}</Box>,
  },
  {
    field: "monthlyPayment",
    headerName: "Monthly Payment",
    width: 120,
    renderCell: (params) => <Box sx={clickableCellStyle}>{params.value}</Box>,
  },
  {
    field: "verifiedRemainingBalance",
    headerName: "Verified Remaining Balance",
    width: 130,
    editable: true,
    type: "number",
    renderCell: (params) => {
      const value = Number(params.value);
      const hasValue = !isNaN(value) && value !== 0;
      const latestBalance = params.row.latestVerifiedRemainingBalance;
      const verifiedRemainingBalanceDate = params.row.verifiedRemainingBalanceDate;
      
      const hasLatestBalance =
        latestBalance !== undefined && latestBalance !== null;

      const formattedValue = hasValue ? formatCurrencyValue(value) : "-";
      const formattedLatestBalance = hasLatestBalance
        ? `$${formatCurrencyValue(latestBalance)}`
        : null;
      const tooltipContent = (
        <Box>
          <Typography variant="caption" display="block">
            {formatDate(verifiedRemainingBalanceDate)}
          </Typography>
          {formattedLatestBalance ? (
            <Typography variant="caption" display="block">
              Latest Verified Balance: {formattedLatestBalance}
            </Typography>
          ) : (
            <Typography variant="caption" display="block">
              No verified balance recorded
            </Typography>
          )}
        </Box>
      );

      const cellContent = (
        <Box
          sx={clickableCellStyle}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {formattedValue}
        </Box>
      );

      return (
        <Tooltip title={tooltipContent} placement="top-start">
          {cellContent}
        </Tooltip>
      );
    },
  },
  {
    field: "estimatedRemainingBalance",
    headerName: "Estimated Remaining Balance",
    width: 130,
    valueFormatter: (v) =>
      Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 }),
  },
  {
    field: "estimatedEndDate",
    headerName: "Estimated End Date",
    width: 150,
    valueFormatter: (value) => formatDate(value as string),
  },
  {
    field: "actions",
    headerName: "",
    width: 200,
    renderCell: (params) => <UpdateLoanPopup loan={params.row} />,
  },
  {
    field: "delete",
    headerName: "",
    width: 130,
    renderCell: (params) => (
      <ConfirmActionButton
        id={params.row.id}
        entityType="Loan"
        actionType="delete"
        entityLabel="loan"
        queryKey="loans"
        buttonLabel="Delete"
        buttonColor="error"
      />
    ),
  },
];
